import { Link } from "react-router-dom";
import { ShieldCheck, FileBadge, Landmark, Scale, ArrowUpRight, Download } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const naics = [
  ["484110", "General Freight Trucking, Local"],
  ["484121", "Long-Distance Freight, Truckload"],
  ["493110", "General Warehousing & Storage"],
  ["531120", "Lessors of Nonresidential Buildings"],
  ["541614", "Process & Logistics Consulting"],
  ["236220", "Commercial Building Construction"],
];

const credentials = [
  { icon: ShieldCheck, k: "Federal Compliance", v: "SAM.gov ready · DUNS / UEI active" },
  { icon: FileBadge, k: "Small Business Designation", v: "Minority Woman-Owned (MWBE)" },
  { icon: Scale, k: "Set-Aside Eligibility", v: "WOSB · DBE · 8(a) — pursuing" },
  { icon: Landmark, k: "Contract Vehicles", v: "GSA / Prime Subcontract aligned" },
];

export default function Government() {
  useSEO({
    title: "Government Contracting — Tandem T Holdings",
    description: "Federal, state, and municipal contracting capabilities across logistics and real estate services. Minority woman-owned business enterprise. SAM.gov aligned.",
    path: "/government",
  });
  return (
    <div data-testid="page-government">
      <PageHero
        testId="government-hero"
        overline="Capabilities — Government Contracting"
        title={<>Mission-ready. <em className="italic">Federally aligned.</em></>}
        subtitle="Tandem T Holdings is structured to deliver logistics, real estate, and operational services for federal, state and municipal agencies — with the compliance, transparency and accountability the work demands."
        image="https://images.unsplash.com/photo-1592554310824-4b259d980112"
      />

      {/* CAPABILITIES STATEMENT BLOCK */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <p className="overline text-white/55">Capabilities Statement</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight">
              Built for the standards of federal procurement.
            </h2>
            <p className="text-white/65 font-light leading-relaxed mt-6 max-w-md">
              From freight execution to facility operations, Tandem T delivers the documentation, controls
              and operational maturity that federal contracting officers expect of trusted partners.
            </p>
            <Link
              data-testid="gov-cta-capabilities"
              to="/contact?intent=government"
              className="group inline-flex items-center gap-3 mt-10 overline px-5 py-3 bg-white text-black hover:bg-white/85"
            >
              <Download size={14} /> Request Capabilities Statement
            </Link>
          </Reveal>

          <div className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {credentials.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.k} delay={i * 0.06} className="bg-black">
                  <div className="p-8 md:p-10 min-h-[180px]">
                    <Icon size={20} className="text-white" />
                    <p className="overline text-white/45 mt-6">{c.k}</p>
                    <p className="font-serif text-white text-xl md:text-2xl mt-3 font-light leading-snug">{c.v}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* NAICS CODES */}
      <section className="py-24 md:py-28 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">NAICS Alignment</p>
            <h2 className="font-serif font-light text-white text-3xl md:text-5xl mt-4 leading-tight">
              The codes we operate against.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {naics.map(([code, label], i) => (
              <Reveal key={code} delay={i * 0.04} className="bg-black">
                <div className="p-8 flex items-baseline gap-6">
                  <p className="font-serif text-white text-2xl md:text-3xl font-light w-24">{code}</p>
                  <p className="text-white/65 text-sm font-light">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-6">
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl leading-tight">
              Why agencies choose Tandem T.
            </h2>
          </Reveal>
          <div className="md:col-span-6 space-y-8">
            {[
              ["Integrated Logistics + Real Estate", "Rare end-to-end coverage from freight execution through facility tenancy — under one accountable owner."],
              ["Minority Woman-Owned Leadership", "Direct contribution to agency-level supplier diversity and inclusion goals."],
              ["Operational Transparency", "Federally-aligned reporting cadence and documentation discipline."],
              ["Community Reinvestment", "Every contract advances workforce opportunity within the communities we serve."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="border-t border-white/15 pt-6 grid grid-cols-1 md:grid-cols-12 gap-4">
                  <p className="overline text-white/40 md:col-span-1">0{i + 1}</p>
                  <h3 className="font-serif text-white text-2xl md:text-3xl font-light md:col-span-5">{t}</h3>
                  <p className="text-white/65 font-light md:col-span-6 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <Reveal>
            <h3 className="font-serif font-light text-white text-3xl md:text-4xl max-w-2xl leading-tight">
              Contracting officers, primes, and program leads — let's talk.
            </h3>
          </Reveal>
          <Reveal delay={0.08}>
            <Link
              data-testid="gov-cta-contact"
              to="/contact?intent=government"
              className="group inline-flex items-center gap-3 px-6 py-4 border border-white/30 text-white overline hover:bg-white hover:text-black transition-colors"
            >
              Initiate Contact <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
